import { useEffect, useMemo, useRef, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { httpsCallable } from 'firebase/functions';
import {
  ArrowLeft,
  Eye,
  FileDown,
  Loader2,
  Minus,
  Plus,
  Printer,
  Save,
  Send,
  Share2,
} from 'lucide-react';
import { PageHeader } from '@/components/PageHeader';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { toast } from '@/components/ui/toaster';
import { useAuth } from '@/features/auth/AuthProvider';
import { functions } from '@/lib/firebase';
import { createQuotation, fetchQuotation, updateQuotation } from './quotationQueries';
import { fetchAllCustomers } from '@/features/crm/customerQueries';
import { formatCurrency, generateShareToken } from '@/lib/utils';
import {
  generateQuotationPdfClient,
  type QuotationPdfData,
} from '@/lib/generateQuotationPdfClient';
import type { Customer, Quotation } from '@/types/schemas';
import { logActivity } from '@/lib/activityLog';

type LineItem = {
  id: string;
  description: string;
  quantity: number;
  per: string;
  rate: number;
  amount: number;
};

type SaveRequest = {
  status: 'draft' | 'final';
  silent?: boolean;
  generatePdf?: boolean;
};

const DEFAULT_PER_OPTIONS = ['SqF', 'SqM', 'Piece'];
const PER_OPTIONS_STORAGE_KEY = 'chhabighar.quotationPerOptions';

function makeId() {
  return Math.random().toString(36).slice(2, 9);
}

function emptyLine(): LineItem {
  return {
    id: makeId(),
    description: '',
    quantity: 0,
    per: 'Piece',
    rate: 0,
    amount: 0,
  };
}

function readPerOptions(): string[] {
  try {
    const saved = JSON.parse(localStorage.getItem(PER_OPTIONS_STORAGE_KEY) ?? '[]');
    if (Array.isArray(saved)) {
      return [...new Set([...DEFAULT_PER_OPTIONS, ...saved.filter((v) => typeof v === 'string')])];
    }
  } catch {
    // Ignore invalid local storage and use the defaults.
  }
  return DEFAULT_PER_OPTIONS;
}

function timestampToDate(value: unknown): Date {
  if (value && typeof value === 'object' && 'toDate' in value) {
    return (value as { toDate: () => Date }).toDate();
  }
  if (typeof value === 'number') return new Date(value);
  return new Date();
}

export function QuotationBuilderPage() {
  const { quotationId } = useParams();
  const isEditing = !!quotationId;
  const { user } = useAuth();
  const qc = useQueryClient();

  const { data: existing } = useQuery({
    queryKey: ['quotation', quotationId],
    queryFn: () => (quotationId ? fetchQuotation(quotationId) : Promise.resolve(null)),
    enabled: isEditing,
  });

  const [lines, setLines] = useState<LineItem[]>([emptyLine()]);
  const [customerName, setCustomerName] = useState('');
  const [customerId, setCustomerId] = useState('');
  const [customerPhone, setCustomerPhone] = useState('');
  const [customerAddress, setCustomerAddress] = useState('');
  const [discount, setDiscount] = useState(0);
  const [deliveryCharge, setDeliveryCharge] = useState(0);
  const [notes, setNotes] = useState('');
  const [savedId, setSavedId] = useState<string | null>(null);
  const [quotationNumber, setQuotationNumber] = useState<string | null>(null);
  const [shareToken, setShareToken] = useState<string | null>(null);
  const [status, setStatus] = useState<'draft' | 'final'>('draft');
  const [perOptions, setPerOptions] = useState<string[]>(readPerOptions);
  const [isHydrated, setIsHydrated] = useState(!isEditing);
  const [isPreviewOpen, setIsPreviewOpen] = useState(false);
  const [previewUrl, setPreviewUrl] = useState<string | null>(null);
  const skipNextAutosave = useRef(false);
  const lastAttemptedSignature = useRef('');

  const { data: customers = [] } = useQuery({
    queryKey: ['customers'],
    queryFn: fetchAllCustomers,
  });

  const subtotal = useMemo(
    () => lines.reduce((sum, line) => sum + line.quantity * line.rate, 0),
    [lines],
  );
  const total = Math.max(0, subtotal + deliveryCharge - discount);
  const hasItems = lines.some((line) => line.description.trim() !== '');
  const hasMeaningfulContent =
    hasItems ||
    !!customerName.trim() ||
    !!customerPhone.trim() ||
    !!customerAddress.trim() ||
    !!notes.trim() ||
    deliveryCharge > 0 ||
    discount > 0;

  const autosaveSignature = JSON.stringify({
    customerName,
    customerPhone,
    customerAddress,
    lines,
    deliveryCharge,
    discount,
    notes,
    status,
  });

  useEffect(() => {
    if (!existing) return;
    const mapped: LineItem[] = (existing.items ?? []).map((item) => ({
      id: item.itemId || makeId(),
      description: item.itemName,
      quantity: item.quantity,
      per: item.per || 'Piece',
      rate: item.price,
      amount: item.quantity * item.price,
    }));
    skipNextAutosave.current = true;
    setLines(mapped.length ? mapped : [emptyLine()]);
    setDiscount(existing.discount ?? 0);
    setDeliveryCharge(existing.deliveryCharge ?? 0);
    setNotes(existing.notes ?? '');
    setSavedId(existing.id);
    setQuotationNumber(existing.quotationNumber ?? null);
    setShareToken(existing.shareToken ?? null);
    setStatus(existing.status ?? 'draft');
    setCustomerName(existing.customerSnapshot?.name ?? '');
    setCustomerId(existing.customerId ?? '');
    setCustomerPhone(existing.customerSnapshot?.phone ?? '');
    setCustomerAddress(existing.customerSnapshot?.address ?? '');
    setIsHydrated(true);
  }, [existing]);

  function updateLine(id: string, patch: Partial<LineItem>) {
    setLines((current) =>
      current.map((line) => {
        if (line.id !== id) return line;
        const next = { ...line, ...patch };
        next.amount = next.quantity * next.rate;
        return next;
      }),
    );
  }

  function addPerOption(lineId: string) {
    const value = window.prompt('Enter a new Per unit (for example: Box, Set, Rft)');
    const trimmed = value?.trim();
    if (!trimmed) return;
    const nextOptions = perOptions.some((option) => option.toLowerCase() === trimmed.toLowerCase())
      ? perOptions
      : [...perOptions, trimmed];
    setPerOptions(nextOptions);
    localStorage.setItem(PER_OPTIONS_STORAGE_KEY, JSON.stringify(nextOptions));
    updateLine(lineId, { per: trimmed });
  }

  function buildPdfData(): QuotationPdfData {
    return {
      businessName: 'CHHABIGHAR',
      tagline: 'Powered by NexaRise',
      quotationNumber: quotationNumber ?? 'DRAFT',
      date: timestampToDate(existing?.createdAt),
      customerName: customerName.trim() || 'Valued Customer',
      customerPhone: customerPhone.trim(),
      customerAddress: customerAddress.trim() || undefined,
      items: lines
        .filter((line) => line.description.trim())
        .map((line) => ({
          itemName: line.description.trim(),
          sku: '',
          quantity: line.quantity,
          per: line.per,
          price: line.rate,
          total: line.quantity * line.rate,
        })),
      subtotal,
      deliveryCharge,
      discount,
      total,
      notes: notes.trim() || undefined,
    };
  }

  function handlePreview() {
    try {
      const url = generateQuotationPdfClient(buildPdfData(), 'preview');
      if (url) {
        setPreviewUrl(url);
        setIsPreviewOpen(true);
      }
    } catch (error) {
      toast({ title: 'Preview failed', description: String(error), variant: 'destructive' });
    }
  }

  const save = useMutation({
    mutationFn: async ({ status: nextStatus, generatePdf }: SaveRequest) => {
      if (generatePdf && !customerName.trim()) throw new Error('Customer name is required');
      if (generatePdf && !customerPhone.trim()) throw new Error('Customer phone is required');
      if (generatePdf && !hasItems) throw new Error('Add at least one product');

      const token =
        nextStatus === 'final' && !shareToken ? generateShareToken() : shareToken ?? null;
      const payload: Omit<Quotation, 'id'> = {
        ...(customerId ? { customerId } : {}),
        customerSnapshot: {
          name: customerName.trim(),
          phone: customerPhone.trim(),
          address: customerAddress.trim(),
        },
        items: lines
          .filter((line) => line.description.trim())
          .map((line) => ({
            itemId: line.id,
            itemName: line.description.trim(),
            sku: '',
            price: line.rate,
            quantity: line.quantity,
            per: line.per,
            total: line.quantity * line.rate,
          })),
        subtotal,
        deliveryCharge,
        discount,
        totalAmount: total,
        status: nextStatus,
        notes: notes.trim(),
        ...(token ? { shareToken: token } : {}),
        ...(quotationNumber ? { quotationNumber } : {}),
      };

      let id = savedId;
      let assignedNumber = quotationNumber;
      if (id) {
        await updateQuotation(id, payload);
      } else {
        const created = await createQuotation(payload, user?.uid ?? null);
        id = created.id;
        assignedNumber = created.quotationNumber;
      }

      let assignedToken: string | null = token;
      if (generatePdf) {
        try {
          const fn = httpsCallable<
            { quotationId: string },
            { quotationNumber: string; shareToken?: string }
          >(functions, 'generateQuotationPdf');
          const result = await Promise.race([
            fn({ quotationId: id }),
            new Promise<never>((_, reject) => {
              window.setTimeout(
                () => reject(new Error('PDF service did not respond. The quotation was still saved.')),
                8_000,
              );
            }),
          ]);
          assignedNumber = result.data.quotationNumber;
          if (result.data.shareToken) assignedToken = result.data.shareToken;
        } catch (error) {
          console.warn('Cloud PDF generation failed; quotation was still saved.', error);
        }
      }

      return { id, status: nextStatus, assignedNumber, assignedToken };
    },
    onSuccess: ({ id, status: nextStatus, assignedNumber, assignedToken }, request) => {
      setSavedId(id);
      setStatus(nextStatus);
      if (assignedNumber) setQuotationNumber(assignedNumber);
      if (assignedToken) setShareToken(assignedToken);
      qc.invalidateQueries({ queryKey: ['quotations'] });
      qc.invalidateQueries({ queryKey: ['quotation', id] });
      qc.invalidateQueries({ queryKey: ['dashboard'] });

      if (!quotationId) {
        logActivity({
          action: 'quotation_created',
          title: 'Quotation Created',
          description: `Created quotation #${assignedNumber || quotationNumber || id} for ${customerName || 'customer'}`,
          actorId: user?.uid ?? null,
          actorName: user?.displayName ?? user?.email ?? null,
          entityType: 'quotation',
          entityId: id,
        });
        window.history.replaceState(window.history.state, '', `/quotations/${id}`);
      }
      if (!request.silent) {
        toast({
          title: nextStatus === 'final' ? 'Quotation finalised' : 'Quotation saved',
          variant: 'success',
        });
      }
    },
    onError: (error, request) => {
      if (!request.silent) {
        toast({ title: 'Save failed', description: String(error), variant: 'destructive' });
      }
    },
  });
  const persistQuotation = save.mutate;

  useEffect(() => {
    if (!isHydrated || !hasMeaningfulContent || save.isPending) return;
    if (skipNextAutosave.current) {
      skipNextAutosave.current = false;
      lastAttemptedSignature.current = autosaveSignature;
      return;
    }
    if (lastAttemptedSignature.current === autosaveSignature) return;

    const timer = window.setTimeout(() => {
      lastAttemptedSignature.current = autosaveSignature;
      persistQuotation({ status, silent: true });
    }, 900);
    return () => window.clearTimeout(timer);
  }, [autosaveSignature, hasMeaningfulContent, isHydrated, persistQuotation, save.isPending, status]);

  function addLine() {
    setLines((current) => [...current, emptyLine()]);
  }

  function removeLine(id: string) {
    setLines((current) => (current.length > 1 ? current.filter((line) => line.id !== id) : current));
  }

  const autosaveLabel = save.isPending
    ? 'Saving…'
    : savedId
      ? 'All changes saved'
      : 'Auto-save on';

  return (
    <div>
      <PageHeader
        title={savedId || isEditing ? 'Edit quotation' : 'New quotation'}
        subtitle={quotationNumber ? `${quotationNumber} · ${autosaveLabel}` : autosaveLabel}
        actions={
          <>
            <Button asChild variant="ghost" size="sm">
              <Link to="/quotations">
                <ArrowLeft className="h-4 w-4" /> Back
              </Link>
            </Button>
            {savedId && shareToken && (
              <Button
                variant="outline"
                size="sm"
                onClick={async () => {
                  await navigator.clipboard.writeText(`${window.location.origin}/q/${shareToken}`);
                  toast({ title: 'Share link copied', variant: 'success' });
                }}
              >
                <Share2 className="h-4 w-4" />
                <span className="hidden sm:inline">Copy link</span>
              </Button>
            )}
            <Button variant="outline" size="sm" onClick={handlePreview} disabled={!hasItems}>
              <Eye className="h-4 w-4" />
              <span className="hidden sm:inline">Preview</span>
            </Button>
            <Button
              variant="outline"
              size="sm"
              onClick={() => save.mutate({ status, silent: false })}
              disabled={save.isPending || !hasMeaningfulContent}
            >
              <Save className="h-4 w-4" />
              <span className="hidden sm:inline">Save now</span>
            </Button>
            <Button
              variant="gold"
              size="sm"
              onClick={() => save.mutate({ status: 'final', generatePdf: true })}
              disabled={save.isPending || !hasItems}
            >
              {save.isPending ? (
                <Loader2 className="h-4 w-4 animate-spin" />
              ) : (
                <Send className="h-4 w-4" />
              )}
              <span className="hidden sm:inline">Finalise</span>
            </Button>
          </>
        }
      />

      <Dialog open={isPreviewOpen} onOpenChange={setIsPreviewOpen}>
        <DialogContent className="flex h-[90vh] max-w-4xl flex-col overflow-hidden p-0">
          <DialogHeader className="border-b p-4">
            <DialogTitle>Quotation Preview</DialogTitle>
          </DialogHeader>
          <div className="relative flex-1 bg-muted">
            {previewUrl ? (
              <iframe src={previewUrl} className="h-full w-full border-0" title="Quotation Preview" />
            ) : (
              <div className="flex h-full items-center justify-center">
                <Loader2 className="h-8 w-8 animate-spin text-primary" />
              </div>
            )}
          </div>
          <div className="flex justify-end gap-2 border-t p-4">
            <Button variant="ghost" onClick={() => setIsPreviewOpen(false)}>Close</Button>
            <Button variant="outline" onClick={() => generateQuotationPdfClient(buildPdfData(), 'print')}>
              <Printer className="h-4 w-4" /> Print
            </Button>
            <Button variant="gold" onClick={() => generateQuotationPdfClient(buildPdfData())}>
              <FileDown className="h-4 w-4" /> Download PDF
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      <div className="mx-auto max-w-5xl space-y-4">
        <Card>
          <CardHeader><CardTitle className="text-base">Customer Details</CardTitle></CardHeader>
          <CardContent className="grid gap-3 sm:grid-cols-2">
            <div className="space-y-1.5 sm:col-span-2">
              <Label className="text-xs">Select existing customer</Label>
              <select
                className="h-10 w-full rounded-md border border-input bg-background px-3 text-sm"
                value={customerId || '__manual__'}
                onChange={(event) => {
                  const id = event.target.value;
                  if (id === '__manual__') {
                    setCustomerId('');
                    return;
                  }
                  const customer = customers.find((item) => item.id === id);
                  if (!customer) return;
                  setCustomerId(customer.id);
                  setCustomerName(customer.name);
                  setCustomerPhone(customer.phone);
                  setCustomerAddress(customer.address ?? '');
                }}
              >
                <option value="__manual__">Enter customer manually</option>
                {customers.map((customer: Customer) => (
                  <option key={customer.id} value={customer.id}>
                    {customer.name} · {customer.phone}
                  </option>
                ))}
              </select>
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs">Customer name <span className="text-destructive">*</span></Label>
              <Input placeholder="e.g. Rahul Sharma" value={customerName} onChange={(e) => { setCustomerId(''); setCustomerName(e.target.value); }} />
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs">Phone number <span className="text-destructive">*</span></Label>
              <Input placeholder="e.g. 9800000000" value={customerPhone} onChange={(e) => { setCustomerId(''); setCustomerPhone(e.target.value); }} />
            </div>
            <div className="space-y-1.5 sm:col-span-2">
              <Label className="text-xs">Customer address</Label>
              <Input placeholder="Optional" value={customerAddress} onChange={(e) => { setCustomerId(''); setCustomerAddress(e.target.value); }} />
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between pb-2">
            <CardTitle className="text-base">Products &amp; Pricing</CardTitle>
            <Button size="sm" variant="outline" onClick={addLine}>
              <Plus className="h-4 w-4" /> Add product
            </Button>
          </CardHeader>
          <CardContent>
            <div className="overflow-x-auto">
              <div className="min-w-[760px] space-y-2">
                <div className="grid grid-cols-12 gap-2 px-1 text-[10px] font-medium uppercase tracking-wider text-muted-foreground">
                  <div className="col-span-4">Product Name / Description</div>
                  <div className="col-span-1 text-center">Qty</div>
                  <div className="col-span-2 text-center">Per</div>
                  <div className="col-span-2 text-right">Rate (₹)</div>
                  <div className="col-span-2 text-right">Amount (₹)</div>
                  <div className="col-span-1" />
                </div>

                {lines.map((line, index) => (
                  <div key={line.id} className="grid grid-cols-12 items-center gap-2">
                    <div className="col-span-4">
                      <Input
                        placeholder={`Product ${index + 1} / description`}
                        value={line.description}
                        onChange={(e) => updateLine(line.id, { description: e.target.value })}
                      />
                    </div>
                    <div className="col-span-1">
                      <Input
                        type="number"
                        min="0"
                        step="any"
                        className="text-center"
                        placeholder="Qty"
                        value={line.quantity || ''}
                        onChange={(e) => updateLine(line.id, { quantity: Number(e.target.value || 0) })}
                      />
                    </div>
                    <div className="col-span-2">
                      <select
                        className="h-9 w-full rounded-md border border-input bg-background px-2 text-sm"
                        value={line.per}
                        onChange={(e) => {
                          if (e.target.value === '__add__') addPerOption(line.id);
                          else updateLine(line.id, { per: e.target.value });
                        }}
                      >
                        {perOptions.map((option) => <option key={option} value={option}>{option}</option>)}
                        <option value="__add__">+ Add unit…</option>
                      </select>
                    </div>
                    <div className="col-span-2">
                      <Input
                        type="number"
                        min="0"
                        step="0.01"
                        className="text-right"
                        placeholder="Rate"
                        value={line.rate || ''}
                        onChange={(e) => updateLine(line.id, { rate: Number(e.target.value || 0) })}
                      />
                    </div>
                    <div className="col-span-2">
                      <Input
                        readOnly
                        className="bg-muted/40 text-right tabular-nums"
                        placeholder="Amount"
                        value={line.amount || ''}
                        aria-label="Calculated amount"
                      />
                    </div>
                    <div className="col-span-1 flex justify-end">
                      <Button
                        variant="ghost"
                        size="icon"
                        className="h-8 w-8"
                        onClick={() => removeLine(line.id)}
                        disabled={lines.length === 1}
                        title="Remove row"
                      >
                        <Minus className="h-3.5 w-3.5 text-destructive" />
                      </Button>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            <div className="ml-auto mt-5 w-full max-w-sm space-y-2 border-t pt-3 text-sm">
              <div className="flex justify-between">
                <span className="text-muted-foreground">Subtotal</span>
                <span className="tabular-nums">{formatCurrency(subtotal)}</span>
              </div>
              <div className="flex items-center justify-between gap-2">
                <span className="text-muted-foreground">Delivery Charge ₹</span>
                <Input
                  type="number"
                  className="h-8 w-44 text-right"
                  value={deliveryCharge || ''}
                  min="0"
                  placeholder="0.00"
                  onChange={(e) => setDeliveryCharge(Number(e.target.value || 0))}
                />
              </div>
              <div className="flex items-center justify-between gap-2">
                <span className="text-muted-foreground">Discount ₹</span>
                <Input
                  type="number"
                  className="h-8 w-44 text-right"
                  value={discount || ''}
                  min="0"
                  placeholder="0.00"
                  onChange={(e) => setDiscount(Number(e.target.value || 0))}
                />
              </div>
              <div className="flex justify-between border-t pt-2 font-serif text-lg">
                <span>Total</span>
                <span className="tabular-nums">{formatCurrency(total)}</span>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="pt-4">
            <div className="space-y-1.5">
              <Label className="text-xs">Notes (optional)</Label>
              <Textarea rows={2} placeholder="Terms, validity, delivery info…" value={notes} onChange={(e) => setNotes(e.target.value)} />
            </div>
          </CardContent>
        </Card>

        <div className="flex flex-wrap justify-end gap-2">
          <Button variant="outline" disabled={!hasItems} onClick={() => generateQuotationPdfClient(buildPdfData(), 'print')}>
            <Printer className="h-4 w-4" /> Print
          </Button>
          <Button variant="gold" disabled={!hasItems} onClick={() => generateQuotationPdfClient(buildPdfData())}>
            <FileDown className="h-4 w-4" /> Download PDF
          </Button>
        </div>
      </div>
    </div>
  );
}
